/**
 * Bulk-import spec for the `Session Types` sheet of the organizer workbook.
 *
 * One row per session category for the event. Together these populate
 * `Event.config.sessionTypes[]`, the per-event session category config
 * the admin manages via SessionTypeManager. Categories drive:
 *   - the color chips shown on Session cards (backgroundColor / textColor)
 *   - the icon shown in mobile/admin agenda surfaces (iconName)
 *   - the agenda's category filter chips
 *   - default audience for new sessions of that category (defaultAudience)
 *
 * `id` on Event.config.sessionTypes[i] is server-derived from a slug of
 * `name` (lowercase + non-alphanumerics to hyphens), matching the existing
 * convention used by the Sessions sheet's `sessionTypeName` resolution.
 */
import { invalid, pending, type ImportSpec, type ParsedRow } from "./importSpec";
import { normalize } from "./importTypes";
import { SESSION_TYPE_ICON_NAMES } from "../constants";

/** Hex color regex: requires #RRGGBB form (case-insensitive). */
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

export interface RawSessionTypeImportRow
  extends Record<string, string | undefined> {
  name?: string;
  backgroundColor?: string;
  textColor?: string;
  iconName?: string;
  defaultAudience?: string;
}

export interface ParsedSessionTypeImportRow {
  /** Display name; the server slugifies this for `Event.config.sessionTypes[i].id`. */
  name: string;
  /** #RRGGBB. Drives the agenda chip background. */
  backgroundColor: string;
  /** #RRGGBB. Drives the agenda chip text. */
  textColor: string;
  /** Lucide icon name from the curated session type icon set. */
  iconName: string | null;
  /**
   * Optional audience name reference (case-insensitive match against
   * this event's audiences). Null = no default; the admin session
   * compose form requires the operator to pick an audience for each new
   * session of this category.
   */
  defaultAudience: string | null;
}

export const sessionTypeImportSpec: ImportSpec<
  RawSessionTypeImportRow,
  ParsedSessionTypeImportRow
> = {
  sheetName: "Session Types",
  purpose:
    "Per-event session categories (Presentation, Meal, Activity, etc.). One row per category. Drives the agenda chip colors and the default audience for new sessions of each category. Matches the admin Event form's Session Types editor.",
  columns: [
    { key: "name", note: "required - category display name" },
    {
      key: "backgroundColor",
      note: "required - #RRGGBB hex (e.g. #0F2857)",
    },
    {
      key: "textColor",
      note: "required - #RRGGBB hex (e.g. #FFFFFF)",
    },
    {
      key: "iconName",
      note:
        "optional - Lucide icon name from the app's curated set (e.g. BarChart3, Utensils, Users)",
    },
    {
      key: "defaultAudience",
      note:
        "optional - default audience name for new sessions in this category (e.g. 'All Members'). Must match an audience created for this event.",
    },
  ],
  example: {
    name: "Presentation",
    backgroundColor: "#0F2857",
    textColor: "#FFFFFF",
    iconName: "BarChart3",
    defaultAudience: "All Members",
  },
  validate(raw, lineNumber) {
    const name = normalize(raw.name);
    const backgroundColor = normalize(raw.backgroundColor);
    const textColor = normalize(raw.textColor);
    const iconName = normalize(raw.iconName) || null;
    const defaultAudience = normalize(raw.defaultAudience) || null;

    const base: Omit<
      ParsedRow<ParsedSessionTypeImportRow>,
      "status" | "message"
    > = {
      lineNumber,
      data: {
        name,
        backgroundColor,
        textColor,
        iconName,
        defaultAudience,
      },
    };

    if (!name) return invalid(base, "Name is required");
    if (!backgroundColor) {
      return invalid(base, "backgroundColor is required");
    }
    if (!HEX_COLOR_PATTERN.test(backgroundColor)) {
      return invalid(
        base,
        `Invalid backgroundColor "${backgroundColor}". Must be #RRGGBB hex.`,
      );
    }
    if (!textColor) return invalid(base, "textColor is required");
    if (!HEX_COLOR_PATTERN.test(textColor)) {
      return invalid(
        base,
        `Invalid textColor "${textColor}". Must be #RRGGBB hex.`,
      );
    }
    if (iconName && !SESSION_TYPE_ICON_NAMES.includes(iconName)) {
      return invalid(
        base,
        `Invalid iconName "${iconName}". Must match a curated session type icon name.`,
      );
    }

    return pending(base);
  },
};
